import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { contractDetailsPageQueryOptions } from "~/app/api/pageData/contractDetailsPageQuery";
import type { ContractDetailsPageInitialData } from "~/app/api/pageData/contractDetailsPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  creatContractDetailsPage as createContractDetailsPageComponent,
  type ContractDetailsPageProps,
} from "~/pages/ContractDetailsPage/ContractDetailsPage";

/**
 * Reproduces the legacy controller-built {@code reactPageProps} rename that fed
 * the {@code ContractDetailsPageComponent} island via {@code createComponentAtId}:
 * {@code contractCode}->{@code contractNo}, {@code materialNumber}->{@code materialNo},
 * {@code packCode}->{@code packageCode}. {@code contractPosition} keeps its name
 * and is forwarded as the required number bound from the fourth route segment.
 */
function toContractDetailsProps(data: ContractDetailsPageInitialData | undefined): ContractDetailsPageProps {
  if (!data) {
    throw new Error("Contract-details page-data response is missing its required body");
  }
  return {
    contractNo: data.contractCode,
    materialNo: data.materialNumber,
    packageCode: data.packCode,
    contractPosition: data.contractPosition,
  };
}

function ContractDetailsRoute() {
  const result = useLoaderData({
    from: "/my-account/contractDetails/$contractCode/$materialNumber/$packCode/$contractPosition",
  }) as PageInitialData<ContractDetailsPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createContractDetailsPageComponent(toContractDetailsProps(result.data))}
    </StorefrontShellFrame>
  );
}

export async function loadContractDetailsPageData(
  context: { queryClient: QueryClient },
  contractCode: string,
  materialNumber: string,
  packCode: string,
  contractPosition: string,
) {
  const result = await context.queryClient.ensureQueryData(
    contractDetailsPageQueryOptions(contractCode, materialNumber, packCode, contractPosition),
  );

  return unwrapPageInitialData(result);
}

export const contractDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/my-account/contractDetails/$contractCode/$materialNumber/$packCode/$contractPosition",
  loader: async ({ context, params }) =>
    loadContractDetailsPageData(
      context,
      params.contractCode,
      params.materialNumber,
      params.packCode,
      params.contractPosition,
    ),
  component: ContractDetailsRoute,
});
